import {
    SyntheticEvent,
    Component,
    RefObject,
    createRef,
    KeyboardEvent,
    ChangeEvent,
    Fragment,
    InputHTMLAttributes,
} from 'react';
import { Tag, Form } from '@servicetitan/design-system';
import * as Style from './multi-tags-input.module.less';
import classNames from 'classnames';
import { observable, action, runInAction, makeObservable } from 'mobx';
import { observer } from 'mobx-react';
import { InputFieldState } from '@servicetitan/form';
import ReactInputMask, { InputState } from 'react-input-mask';
import { ErrorLabel } from './error-label';

interface MultiTagsInputProps {
    onChangeHandler: (e: SyntheticEvent<HTMLElement>, data: any) => void;
    values?: string[];
    validators?: { (str: string): string | false }[];
    formatter?(data: string): string;
    placeholder?: string;
    mask: string;
    transformValue?(val: string): string;
    onError?: (value: boolean) => void;
    error?: string;
    className?: string;
}

@observer
export class MultiTagsInput extends Component<MultiTagsInputProps> {
    @observable
    hasFocus = false;

    inputField = new InputFieldState('');

    @observable
    values: string[];

    @observable
    isAddTagActionTriggered = false;

    inputRef: RefObject<HTMLInputElement>;

    constructor(p: MultiTagsInputProps) {
        super(p);
        makeObservable(this);
        this.values = JSON.parse(JSON.stringify(p.values)) || [];
        this.inputRef = createRef();
        if (this.props.validators) {
            this.inputField.validators(...this.props.validators);
        }
    }

    @action
    onFocus = () => {
        this.hasFocus = true;
    };

    @action
    onBlur = (e: SyntheticEvent<HTMLElement>) => {
        this.hasFocus = false;
        this.addInputValue(e);
    };

    get showError() {
        return (
            ((!this.hasFocus || this.isAddTagActionTriggered) && this.inputField.hasError) ||
            !!this.props.error
        );
    }

    addInputValue = async (e: SyntheticEvent<HTMLElement>) => {
        await this.inputField.validate();
        this.props.onError?.(this.inputField.hasError);
        runInAction(() => {
            this.isAddTagActionTriggered = true;
            if (!this.inputField.hasError && this.inputField.$ !== '') {
                this.values.push(this.inputField.value.trim());
                this.inputField.reset();
                this.props.onChangeHandler(e, {
                    value: this.values,
                });
            }
        });
    };

    handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
            this.addInputValue(e);
        }
    };

    getRemover = (index: number) => (e: SyntheticEvent<HTMLElement>) => {
        runInAction(() => {
            this.values.splice(index, 1);
            this.props.onChangeHandler(e, { value: this.values });
        });
    };

    onContainerClicked = () => {
        if (this.inputRef.current) {
            this.inputRef.current.focus();
        }
    };

    inputOnChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const value = this.props.transformValue
            ? this.props.transformValue(e.target.value)
            : e.target.value;
        runInAction(() => (this.isAddTagActionTriggered = false));
        this.inputField.onChangeHandler(e, { value });
    };

    beforeMaskedValueChange = (newState: InputState) => {
        const { value } = newState;
        let selection = newState.selection;
        const transformedVal = this.props.transformValue ? this.props.transformValue(value) : value;
        if (transformedVal === '') {
            selection = { start: 0, end: 0 };
        }
        return {
            value,
            selection,
        };
    };

    render() {
        const { formatter, placeholder, mask } = this.props;
        return (
            <Fragment>
                <div
                    onClick={this.onContainerClicked}
                    className={classNames(
                        Style.inputContainer,
                        {
                            [Style.hasFocus]: this.hasFocus,
                            [Style.hasError]: this.showError,
                        },
                        this.props.className
                    )}
                >
                    {this.values.map((val, index) => (
                        <Tag
                            index={index}
                            onClose={this.getRemover(index)}
                            className={Style.tag}
                            key={val}
                            title=""
                        >
                            {formatter ? formatter(val) : val}
                        </Tag>
                    ))}
                    <ReactInputMask
                        mask={mask}
                        placeholder={placeholder}
                        onFocus={this.onFocus}
                        onBlur={this.onBlur}
                        onKeyDown={this.handleKeyDown}
                        onChange={this.inputOnChangeHandler}
                        value={this.inputField.value}
                        beforeMaskedValueChange={this.beforeMaskedValueChange}
                    >
                        {(props: InputHTMLAttributes<HTMLInputElement>) => (
                            <Form.Input
                                small
                                className={classNames(Style.input, {
                                    [Style.error]: this.showError,
                                })}
                                {...props}
                                ref={this.inputRef}
                            />
                        )}
                    </ReactInputMask>
                </div>
                {this.showError && <ErrorLabel error={this.inputField.error || this.props.error} />}
            </Fragment>
        );
    }
}
